import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import appRoutes from '../routes/app';
import { compose, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { loadUserInfo } from 'actions/actionCreators/userActions';
import { loadGlobalParams, resetCurrentOffer } from 'features/params/globalParams/actionCreators';
import { loadStates } from 'actions/actionCreators/dictionariesActions';
import { cacheAssetHierarchy } from 'features/asset/hierarchy/actionCreators';
import { loadGlobalRights } from 'features/globalRights/actionCreators';
import { loadApplicationFeatures } from 'features/applicationFeatures/actionCreators';
import AreasProvider from '../providers/AreasProvider';
import { ParamsContext } from '../providers/ParamsContext';
import { GlobalRightsContext } from '../providers/GlobalRightsContext';
import { ApplicationFeaturesContext } from '../providers/ApplicationFeaturesContext';
import documentTypeIds from 'constants/documentTypeIds';
import rightSidebarDrawers from 'constants/rightSidebarDrawers';
import { ToastContainer } from '@totalsoft/rocket-ui';
import userManager from 'utils/userManager';
import ActivityIndicatorProvider from '../providers/ActivityIndicatorProvider';
import GlobalSubscriptionArea from 'features/common/components/GlobalSubscriptionArea';
import ToastNotifier from './snackbar/ToastNotifier';
import { Container, Content, SideButtonsWrapper } from './AppStyles';
import Sidebar from './layout/sidebar/Sidebar';
import Header from './layout/header/Header';
import Main from './layout/Main';
import RightSideBarButtons from './layout/RightSideBarButtons';
import RightSideBar from './layout/RightSideBar';
import { IdleTimerProvider } from 'react-idle-timer';
import { env } from 'utils/env';

class App extends React.PureComponent {
  constructor(props) {
    super(props);
    this.idleTimer = null;
    this.handleOnIdle = this.handleOnIdle.bind(this);
  }

  state = {
    mobileOpen: false,
    miniActive: true,
    mobileRightDrawers: {
      summaryDrawerOpen: false,
      commentsDrawerOpen: false
    },
    rightSidebarButtonsVisible: false
  };

  handleOnActive(_event) {
    userManager.signoutRedirect();
  }

  handleOnIdle(_event) {
    console.log('user is idle!');
  }

  mainPanelRef = React.createRef();

  handleDrawerToggle = () => {
    this.setState({ mobileOpen: !this.state.mobileOpen });
  };

  handleRightDrawerToggle = (drawerName, closeAll = false) => {
    switch (drawerName) {
      case rightSidebarDrawers.summary:
        this.setState({
          mobileRightDrawers: {
            summaryDrawerOpen: !this.state.mobileRightDrawers.summaryDrawerOpen && !closeAll,
            commentsDrawerOpen: false
          }
        });
        break;
      case rightSidebarDrawers.comments:
        this.setState({
          mobileRightDrawers: {
            summaryDrawerOpen: false,
            commentsDrawerOpen: !this.state.mobileRightDrawers.commentsDrawerOpen && !closeAll
          }
        });
        break;
      default:
        break;
    }
  };

  showRightSideBar = visible => {
    this.setState({
      mobileRightDrawers: {
        summaryDrawerOpen: this.state.mobileRightDrawers.summaryDrawerOpen && visible,
        commentsDrawerOpen: this.state.mobileRightDrawers.commentsDrawerOpen && visible
      },
      rightSidebarButtonsVisible: visible
    });
  };

  componentDidMount() {
    this.loadUserInfo();
    this.loadGlobalParams();
    this.loadGlobalRights();
    this.loadApplicationFeatures();
    this.loadStates();
    this.cacheAssetHierarchy();
  }

  componentDidUpdate(prev) {
    if (prev.history.location.pathname !== prev.location.pathname && this.mainPanelRef?.current != null) {
      this.mainPanelRef.current.scrollTop = 0;
    }

    if (!this.props.oidc.isLoadingUser && (prev.oidc !== this.props.oidc || prev.userInfo !== this.props.userInfo)) {
      this.loadUserInfo();
    }

    const params = this.props.params;
    if (prev.params !== params || prev.oidc.user !== this.props.oidc.user) this.loadGlobalParams();

    const history = this.props.history;
    if (prev.userInfo !== this.props.userInfo && this.props.userInfo.isLoaded && !this.props.userInfo.userExtraInfoModel.hasAccess)
      history.push('/notAllowed');

    const globalRights = this.props.globalRights;
    if (prev.globalRights !== globalRights || prev.oidc.user !== this.props.oidc.user) this.loadGlobalRights();

    const applicationFeatures = this.props.applicationFeatures;
    if (prev.applicationFeatures !== applicationFeatures || prev.oidc.user !== this.props.oidc.user) this.loadApplicationFeatures();
  }

  loadGlobalParams() {
    if (this.props.oidc && this.props.oidc.user) {
      this.props.actions.loadGlobalParams();
    }
  }

  loadGlobalRights() {
    if (this.props.oidc && this.props.oidc.user) {
      this.props.actions.loadGlobalRights();
    }
  }

  loadApplicationFeatures() {
    if (this.props.oidc && this.props.oidc.user) this.props.actions.loadApplicationFeatures();
  }

  resetCurrentOffer() {
    if (this.props.oidc && this.props.oidc.user) {
      this.props.actions.resetCurrentOffer();
    }
  }

  loadStates() {
    if (this.props.oidc && this.props.oidc.user) {
      this.props.actions.loadStates(documentTypeIds.Offer);
    }
  }

  loadUserInfo() {
    if (this.props.oidc && this.props.oidc.user) this.props.actions.loadUserInfo();
  }

  cacheAssetHierarchy() {
    if (this.props.oidc && this.props.oidc.user) {
      this.props.actions.cacheAssetHierarchy();
    }
  }

  changeLanguage = async lng => {
    await this.props.i18n.changeLanguage(lng.target.value);
    this.setState({ mobileOpen: !this.state.mobileOpen });
    this.loadStates();
    this.resetCurrentOffer();
    this.loadGlobalRights();
  };

  sidebarMinimize = () => {
    this.setState({ miniActive: !this.state.miniActive });
  };

  render() {
    const { userInfo } = this.props;

    return (
      <>
        <ToastNotifier />
        <ToastContainer theme='colored' style={{ width: '470px' }} />
        {userInfo.userId && (
          <AreasProvider>
            <IdleTimerProvider
              crossTab
              ref={ref => {
                this.idleTimer = ref;
              }}
              timeout={1000 * env.REACT_APP_IDLE_TIMEOUT}
              onIdle={this.handleOnIdle}
              onActive={this.handleOnActive}
              debounce={500}
            />
            <ApplicationFeaturesContext.Provider value={this.props.applicationFeatures}>
              <GlobalRightsContext.Provider value={this.props.globalRights || {}}>
                <ParamsContext.Provider value={this.props.params.items}>
                  <ActivityIndicatorProvider>
                    <Container>
                      <Sidebar
                        closeDrawer={this.handleDrawerToggle}
                        changeLanguage={this.changeLanguage}
                        drawerOpen={this.state.mobileOpen}
                        drawerMinimized={this.state.miniActive}
                        withGradient={false}
                      />
                      <RightSideBar
                        handleRightDrawerToggle={this.handleRightDrawerToggle}
                        showRightSideBar={this.showRightSideBar}
                        open={this.state.mobileRightDrawers.summaryDrawerOpen || this.state.mobileRightDrawers.commentsDrawerOpen}
                        drawerName={
                          this.state.mobileRightDrawers.summaryDrawerOpen
                            ? rightSidebarDrawers.summary
                            : this.state.mobileRightDrawers.commentsDrawerOpen
                            ? rightSidebarDrawers.comments
                            : ''
                        }
                      />
                      <Content
                        ref={this.mainPanelRef}
                        drawerMinimized={this.state.miniActive}
                        rightSideOpened={
                          this.state.mobileRightDrawers.summaryDrawerOpen || this.state.mobileRightDrawers.commentsDrawerOpen
                        }
                      >
                        <Header
                          drawerMinimized={this.state.miniActive}
                          handleDrawerMinimizeToggle={this.sidebarMinimize}
                          handleDrawerOpenToggle={this.handleDrawerToggle}
                        />
                        {this.state.rightSidebarButtonsVisible && (
                          <SideButtonsWrapper>
                            <RightSideBarButtons handleRightDrawerToggle={this.handleRightDrawerToggle} />
                          </SideButtonsWrapper>
                        )}
                        <Main>{appRoutes}</Main>
                      </Content>
                      <GlobalSubscriptionArea />
                    </Container>
                  </ActivityIndicatorProvider>
                </ParamsContext.Provider>
              </GlobalRightsContext.Provider>
            </ApplicationFeaturesContext.Provider>
          </AreasProvider>
        )}
      </>
    );
  }
}

App.propTypes = {
  i18n: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
  oidc: PropTypes.object.isRequired,
  userInfo: PropTypes.object.isRequired,
  userProfile: PropTypes.object.isRequired,
  params: PropTypes.object,
  globalRights: PropTypes.object,
  applicationFeatures: PropTypes.object,
  history: PropTypes.object.isRequired
};

function mapStateToProps(state) {
  return {
    oidc: state.oidc,
    userInfo: state.userInfo,
    userProfile: state.userProfile,
    params: state.params,
    globalRights: state.globalRights,
    applicationFeatures: state.applicationFeatures
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadUserInfo,
        loadGlobalParams,
        loadGlobalRights,
        loadApplicationFeatures,
        resetCurrentOffer,
        loadStates,
        cacheAssetHierarchy
      },
      dispatch
    ),
    dispatch
  };
}

export default compose(withTranslation(), connect(mapStateToProps, mapDispatchToProps))(App);
